import { MonitorConfig, ReportData } from './types';
import { BasePlugin } from './base-plugin';

export class Monitor {
  private config: MonitorConfig;
  private plugins: Map<string, BasePlugin> = new Map();
  private cache: ReportData[] = [];
  private timer: NodeJS.Timeout | null = null;
  private readonly MAX_CACHE_LENGTH = 10;
  private readonly REPORT_INTERVAL = 5000; // 5秒

  constructor(config: MonitorConfig) {
    this.config = {
      ...config,
      enable: config.enable ?? true,
      sampling: config.sampling ?? 1,
      maxCache: config.maxCache ?? this.MAX_CACHE_LENGTH,
      reportInterval: config.reportInterval ?? this.REPORT_INTERVAL,
    };
    this.init();
  }

  private init(): void {
    if (!this.config.enable) return;
    
    // 初始化插件
    if (this.config.plugins) {
      this.config.plugins.forEach(Plugin => {
        const instance = new Plugin();
        instance.setReport(this.addCache.bind(this));
        instance.init();
        this.plugins.set(instance.name, instance);
      });
    }

    // 页面卸载前发送剩余数据
    window.addEventListener('unload', () => {
      this.report(true);
    });
  }

  public destroy(): void {
    this.plugins.forEach(plugin => plugin.destroy());
    this.plugins.clear();
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  private addCache(data: ReportData): void {
    // 采样
    if (Math.random() > this.config.sampling) return;

    this.cache.push({
      ...data,
      timestamp: Date.now(),
    });

    if (this.cache.length >= this.config.maxCache) {
      this.report();
    } else if (!this.timer) {
      this.timer = setTimeout(() => this.report(), this.config.reportInterval);
    }
  }

  private report(isUnload = false): void {
    if (this.cache.length === 0) return;

    const data = this.cache.slice();
    this.cache = [];

    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }

    const reportData = {
      appId: this.config.appId,
      userId: this.config.userId,
      userAgent: navigator.userAgent,
      data
    };

    // 页面卸载前使用 sendBeacon
    if (isUnload && navigator.sendBeacon) {
      navigator.sendBeacon(this.config.reportUrl, JSON.stringify(reportData));
      return;
    }

    // 普通上报使用 fetch
    fetch(this.config.reportUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(reportData),
      keepalive: true
    }).catch(error => {
      console.error('Report error:', error);
      // 上报失败，重新加入缓存
      this.cache.push(...data);
    });
  }
} 